import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { Check, Newspaper, Plus } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SideNavItem } from '../SideNavItem'
import {
  TARGETING_GROUPS,
  TARGETING_TYPES,
  formatKeyValueTargets,
  formatPiped,
  hasAdvancedRows,
  parseKeyValueTargets,
  parsePiped,
  type Targeting,
  type TargetingTypeDef,
  type TargetingTypeId,
} from '@/services/targeting/targeting'
import { DateTargetEditor } from './DateTargetEditor'
import { DayPartingTargetEditor } from './DayPartingTargetEditor'
import { DeviceTargetEditor } from './DeviceTargetEditor'
import {
  INVENTORY_TIERS,
  InventoryTargetEditor,
  clearInventory,
  countInventory,
  isInventoryNegated,
  readInventory,
  writeInventory,
  type InventoryTier,
} from './InventoryTargetEditor'
import { KeyValueTargetEditor } from './KeyValueTargetEditor'
import { LocationTargetEditor } from './LocationTargetEditor'
import { ObjectTargetEditor } from './ObjectTargetEditor'
import { PipedTargetEditor } from './PipedTargetEditor'
import { KeywordArticlePreview } from './KeywordArticlePreview'
import { SegmentTargetEditor } from './SegmentTargetEditor'
import { SemanticTargetEditor, type PendingSemanticTarget } from './SemanticTargetEditor'
import { TargetPane } from './TargetPane'
import { TargetingTypePicker, type TargetingTypeOption } from './TargetingTypePicker'
import { MatchingLabelTargetEditor } from './MatchingLabelTargetEditor'
import { TokenTargetEditor } from './TokenTargetEditor'
import { ViewabilityTargetEditor } from './ViewabilityTargetEditor'

/** What the editor's right-hand side is showing: one targeting type, or the
 * combined inventory browser that covers site country, publisher, site and
 * ad unit at once. */
export type PaneId = TargetingTypeId | 'inventory'

/** What the main area shows: one pane's editor, or the "add targeting" cards. */
type View = PaneId | 'add'

/** One entry of the editor's left-hand nav. */
interface NavEntry {
  id: PaneId
  group: TargetingTypeDef['group']
  icon: LucideIcon
  label: string
  /** One line on what the type does, shown on its "add targeting" card. */
  help: string
  count: number
  negated: boolean
  /** Removes this entry's targeting from the draft. */
  clear: () => void
}

export interface TargetingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The targeting as it stands; edits are applied to a copy. */
  targeting: Targeting
  /** Called with the edited targeting when the user applies it. */
  onApply: (targeting: Targeting) => void
  /** Types to leave out, e.g. the ones a marketplace product does not allow. */
  hiddenTypes?: TargetingTypeId[]
}

/**
 * The targeting editor: every targeting type down the side, the picked one's
 * editor beside it. Edits are held in a working copy, so closing the dialog
 * without applying leaves the line item untouched.
 */
export function TargetingDialog({
  open,
  onOpenChange,
  targeting,
  onApply,
  hiddenTypes = [],
}: TargetingDialogProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Targeting>(targeting)
  const [view, setView] = useState<View>('add')
  /**
   * The panes listed in the nav on top of the ones already holding something:
   * a type picked from the "add targeting" cards is listed, and stays listed,
   * before anything has been chosen in its editor.
   */
  const [added, setAdded] = useState<PaneId[]>([])
  /** A semantic sentence written but not added, and whether to ask about it. */
  const [pendingSemantic, setPendingSemantic] = useState<PendingSemanticTarget | null>(null)
  const [askPending, setAskPending] = useState(false)

  // Stable, so reporting the pending sentence does not loop the editor's effect.
  const reportPending = useCallback(
    (pending: PendingSemanticTarget | null) => setPendingSemantic(pending),
    []
  )

  const apply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const types = useMemo(
    () => TARGETING_TYPES.filter((def) => !hiddenTypes.includes(def.id)),
    [hiddenTypes]
  )

  // The four inventory tiers are browsed together in one pane, so they are
  // taken out of the type list and shown as the single "Inventory" entry.
  const tiers = useMemo(
    () => INVENTORY_TIERS.filter((tier) => !hiddenTypes.includes(tier)),
    [hiddenTypes]
  )
  const patch = (fields: Partial<Targeting>) => setDraft((current) => ({ ...current, ...fields }))

  const inventory = readInventory(draft)

  /**
   * One nav entry per pane: the combined inventory browser first, then every
   * other targeting type. `count` is what decides whether the entry is already
   * in use and so listed, or still on offer under "Add targeting".
   */
  const entries = useMemo<NavEntry[]>(() => {
    const clearFields = (fields: (keyof Targeting)[]) =>
      setDraft((current) => {
        const next = { ...current }
        for (const field of fields) delete next[field]
        return next
      })

    const inventoryEntry: NavEntry[] = tiers.length
      ? [
          {
            id: 'inventory',
            group: 'inventory',
            icon: Newspaper,
            label: t('targeting.inventory.title'),
            help: t('targeting.inventory.help'),
            count: countInventory(inventory),
            negated: isInventoryNegated(inventory),
            clear: () =>
              clearFields(['siteCountryTarget', 'publisherTarget', 'siteTarget', 'adUnitTarget']),
          },
        ]
      : []

    return [
      ...inventoryEntry,
      ...types
        .filter((def) => !INVENTORY_TIERS.includes(def.id as InventoryTier))
        .map((def) => ({
          id: def.id as PaneId,
          group: def.group,
          icon: def.icon,
          label: t(`targeting.types.${def.id}.plural`),
          help: t(`targeting.types.${def.id}.help`),
          count: def.count(draft),
          negated: !!def.negated?.(draft),
          clear: () => clearFields([def.field]),
        })),
    ]
  }, [draft, t, tiers, types, inventory])

  const entryFor = (id: View) => entries.find((entry) => entry.id === id)

  // Only the panes in use, plus the ones picked from the cards, are listed;
  // the rest are on offer under "Add targeting".
  const listed = useMemo(
    () => entries.filter((entry) => entry.count > 0 || added.includes(entry.id)),
    [entries, added]
  )
  const onOffer = useMemo<TargetingTypeOption[]>(() => entries, [entries])

  // Each opening starts from what the line item currently targets: the types it
  // already targets are the listed ones, and the cards are what is left.
  useEffect(() => {
    if (!open) return
    setDraft(targeting)
    setAdded([])
    setView('add')
  }, [open, targeting])

  // The pane being edited can go away — its targeting is cleared, or its type
  // is hidden — and the cards are then what to fall back to.
  useEffect(() => {
    if (view !== 'add' && !listed.some((entry) => entry.id === view)) setView('add')
  }, [listed, view])

  const add = (id: PaneId) => {
    setAdded((current) => (current.includes(id) ? current : [...current, id]))
    setView(id)
  }

  const remove = (entry: NavEntry) => {
    entry.clear()
    setAdded((current) => current.filter((id) => id !== entry.id))
  }

  // A hidden type must not render its editor, not even for the render before
  // the effect above moves the selection back into range.
  const selected = view === 'add' ? undefined : view
  const showEditor = view !== 'add' && !!entryFor(view)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden bg-card p-0 sm:w-[90vw] sm:max-w-[90vw]">
        <DialogHeader className="border-b p-4">
          <DialogTitle>{t('targeting.title')}</DialogTitle>
          <DialogDescription>{t('targeting.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <nav className="max-h-56 shrink-0 overflow-y-auto border-b bg-background py-1 md:max-h-none md:w-72 md:border-r md:border-b-0">
            {/* Only the targeting in use is listed; everything else is reached
                through the cards behind "Add targeting" at the foot of the
                list. */}
            {!listed.length && (
              <p className="p-3 text-xs text-muted-foreground">{t('targeting.untargeted')}</p>
            )}
            {TARGETING_GROUPS.map((group) => {
              const inGroup = listed.filter((entry) => entry.group === group)
              if (!inGroup.length) return null
              return (
                <div key={group} className="mb-1">
                  <div className="px-3 py-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                    {t(`targeting.groups.${group}`)}
                  </div>
                  {inGroup.map((entry) => (
                    <SideNavItem
                      key={entry.id}
                      icon={entry.icon}
                      label={entry.label}
                      count={entry.count}
                      negated={entry.negated}
                      active={selected === entry.id}
                      onSelect={() => add(entry.id)}
                      onRemove={() => remove(entry)}
                    />
                  ))}
                </div>
              )
            })}
            <div className="p-2">
              <Button
                type="button"
                variant={view === 'add' ? 'secondary' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setView('add')}
                disabled={!entries.length}
              >
                <Plus className="size-4" />
                {t('targeting.addTargeting')}
              </Button>
            </div>
          </nav>

          <div className="min-w-0 flex-1 overflow-y-auto p-4">
            {view === 'add' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold">{t('targeting.addTargeting')}</h3>
                  <p className="text-xs text-muted-foreground">{t('targeting.addTargetingHelp')}</p>
                </div>
                <TargetingTypePicker
                  options={onOffer}
                  onSelect={(id) => add(id as PaneId)}
                  emptyMessage={t('targeting.noTypesAvailable')}
                />
              </div>
            )}
            {selected && showEditor && (
              <TargetPane
                type={selected}
                actions={
                  // Empties the browser but keeps it open, unlike removing the
                  // inventory from the list on the left.
                  selected === 'inventory' &&
                  countInventory(inventory) > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDraft((current) =>
                          writeInventory(current, clearInventory(readInventory(current)))
                        )
                      }
                    >
                      {t('targeting.removeAll')}
                    </Button>
                  )
                }
              >
                {selected === 'inventory' && (
                  <InventoryTargetEditor
                    value={inventory}
                    onChange={(value) => setDraft((current) => writeInventory(current, value))}
                    tiers={tiers}
                  />
                )}
                {selected === 'siteGroup' && (
                  <ObjectTargetEditor
                    type="siteGroup"
                    value={{
                      items: draft.siteGroupTarget?.siteGroups ?? [],
                      negated: !!draft.siteGroupTarget?.negated,
                    }}
                    onChange={({ items, negated }) =>
                      patch({
                        siteGroupTarget: { ...draft.siteGroupTarget, siteGroups: items, negated },
                      })
                    }
                  />
                )}
                {selected === 'adUnitMatchingLabel' && (
                  <MatchingLabelTargetEditor
                    value={(draft.adUnitMatchingLabelTargets ?? []).flatMap(
                      (row) => row.matchingLabels ?? []
                    )}
                    onChange={(labels) =>
                      patch({
                        // One target per label: the ad server requires every
                        // label within a target and any one target overall, so
                        // a row each is what makes the selection alternatives.
                        adUnitMatchingLabelTargets: labels.map((label) => ({
                          matchingLabels: [label],
                        })),
                      })
                    }
                  />
                )}
                {selected === 'userSegment' && (
                  <SegmentTargetEditor
                    value={draft.userSegmentTargets ?? []}
                    onChange={(userSegmentTargets) => patch({ userSegmentTargets })}
                    targeting={draft}
                  />
                )}
                {selected === 'namedLocation' && (
                  <LocationTargetEditor
                    value={{
                      locations: draft.namedLocationTarget?.locations ?? [],
                      municipalities: draft.namedLocationTarget?.municipalities ?? [],
                      negated: !!draft.namedLocationTarget?.negated,
                    }}
                    onChange={(value) => patch({ namedLocationTarget: value })}
                  />
                )}
                {selected === 'device' && (
                  <DeviceTargetEditor
                    value={draft.deviceTargets ?? []}
                    onChange={(deviceTargets) => patch({ deviceTargets })}
                  />
                )}
                {selected === 'keyword' && (
                  <div className="flex flex-col gap-4">
                    <PipedTargetEditor
                      type="keyword"
                      value={parsePiped(draft.keywordTargets?.[0]?.keywords)}
                      advanced={hasAdvancedRows(draft.keywordTargets)}
                      onChange={(values) =>
                        patch({
                          // Only the first row is editable here; anything beyond it
                          // is logic this editor cannot express, so it is left be.
                          keywordTargets: [
                            { ...(draft.keywordTargets?.[0] ?? {}), keywords: formatPiped(values) },
                            ...(draft.keywordTargets ?? []).slice(1),
                          ],
                        })
                      }
                    />
                    {/* What the keywords above reach, as the old UI previews it. */}
                    <KeywordArticlePreview
                      values={parsePiped(draft.keywordTargets?.[0]?.keywords)}
                      targeting={draft}
                    />
                  </div>
                )}
                {selected === 'category' && (
                  <PipedTargetEditor
                    type="category"
                    value={parsePiped(draft.categoryTargets?.[0]?.categories)}
                    advanced={hasAdvancedRows(draft.categoryTargets)}
                    onChange={(values) =>
                      patch({
                        categoryTargets: [
                          {
                            ...(draft.categoryTargets?.[0] ?? {}),
                            categories: formatPiped(values),
                          },
                          ...(draft.categoryTargets ?? []).slice(1),
                        ],
                      })
                    }
                  />
                )}
                {selected === 'semantic' && (
                  <SemanticTargetEditor
                    value={draft.semanticTargets ?? []}
                    onChange={(semanticTargets) => patch({ semanticTargets })}
                    targeting={draft}
                    onPendingChange={reportPending}
                  />
                )}
                {selected === 'keyValue' && (
                  <KeyValueTargetEditor
                    value={parseKeyValueTargets(draft.keyValueTargets)}
                    onChange={(groups) => patch({ keyValueTargets: formatKeyValueTargets(groups) })}
                  />
                )}
                {selected === 'url' && (
                  <TokenTargetEditor
                    type="url"
                    value={{
                      values: draft.articleTarget?.urls ?? [],
                      negated: !!draft.articleTarget?.negated,
                    }}
                    onChange={({ values, negated }) =>
                      patch({ articleTarget: { ...draft.articleTarget, urls: values, negated } })
                    }
                  />
                )}
                {selected === 'domain' && (
                  <TokenTargetEditor
                    type="domain"
                    value={{
                      values: draft.domainNameTarget?.names ?? [],
                      negated: !!draft.domainNameTarget?.negated,
                    }}
                    onChange={({ values, negated }) =>
                      patch({
                        domainNameTarget: { ...draft.domainNameTarget, names: values, negated },
                      })
                    }
                  />
                )}
                {selected === 'ipAddress' && (
                  <TokenTargetEditor
                    type="ipAddress"
                    value={{ values: draft.ipAddressTarget?.addresses ?? [] }}
                    onChange={({ values }) =>
                      patch({ ipAddressTarget: { ...draft.ipAddressTarget, addresses: values } })
                    }
                  />
                )}
                {selected === 'viewability' && (
                  <ViewabilityTargetEditor
                    value={draft.viewabilityTarget?.viewability}
                    onChange={(viewability) =>
                      patch({ viewabilityTarget: { ...draft.viewabilityTarget, viewability } })
                    }
                  />
                )}
                {selected === 'dayParting' && (
                  <DayPartingTargetEditor
                    value={draft.dayPartingTargets ?? []}
                    onChange={(dayPartingTargets) => patch({ dayPartingTargets })}
                  />
                )}
                {selected === 'date' && (
                  <DateTargetEditor
                    value={{
                      dateRanges: draft.dateTarget?.dateRanges ?? [],
                      timeZoneSetting: draft.dateTarget?.timeZoneSetting,
                    }}
                    onChange={(dateTarget) => patch({ dateTarget })}
                  />
                )}
              </TargetPane>
            )}
          </div>
        </div>

        <DialogFooter className="border-t p-4">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (pendingSemantic) {
                setAskPending(true)
                return
              }
              apply()
            }}
          >
            <Check className="size-4" />
            {t('targeting.apply')}
          </Button>
        </DialogFooter>

        {/* "Apply targeting" does not add what is still in the composer, so
            say so rather than dropping the sentence without a word. */}
        <AlertDialog open={askPending} onOpenChange={setAskPending}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('targeting.semantic.pendingTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('targeting.semantic.pendingBody', { sentence: pendingSemantic?.sentence })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAskPending(false)
                  apply()
                }}
              >
                {t('targeting.semantic.pendingApplyAnyway')}
              </Button>
              <AlertDialogAction
                onClick={() => {
                  pendingSemantic?.add()
                  setAskPending(false)
                }}
              >
                <Plus className="size-4" />
                {t('targeting.semantic.pendingAdd')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  )
}
